import { faSearch, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import HabitatStatus from 'Components/HabitatCard/HabitatStatus';
import { differenceInMinutes } from 'date-fns';
import { Box, Grommet, grommet, TextInput } from 'grommet';
import { deepMerge } from 'grommet/utils';
import _ from 'lodash-es';
import { h } from 'preact';
import { memo } from 'preact/compat';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { buildURL } from 'Shared/fetch';
import useFetch from 'use-http';

import { routeToHabitat } from '@/components/HabitatLink/utils';
import { useIsMobileLayout } from '@/components/LayoutDetector/hooks';
import zoolifeTheme from '@/grommetTheme';
import { searchHabitats } from '@/helpers/habitatsSearch';
import { useWindowResize } from '@/hooks';
import route from '@/lib/route';
import { setHabitats } from '@/redux/actions';
import { useSelector } from '@/redux/helper';
import { createSortedHabitatsSelector, selectZoosFromHabitats } from '@/redux/selectors/habitats';
import { selectFavoriteHabitatIds } from '@/redux/selectors/users';
import { trackMP } from '@/shared/mp';

import style from './style.scss';

const theme = deepMerge(grommet, zoolifeTheme, {
  global: {
    drop: {
      shadowSize: 'medium',
      extend: `
        border-bottom-left-radius: 12px;
        border-bottom-right-radius: 12px;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        overflow: hidden;
      `,
    },
  },
  button: {
    extend: 'border-radius: 0;',
  },
});

/**
 * Function for rendering a search component with various functionalities.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.className]
 * @param {'left' | 'right'} [props.iconsPosition]
 * @param {boolean} [props.switchableSearch]
 * @param {boolean} [props.searchShow]
 * @param {(val: boolean) => void} [props.setSearchShow]
 * @param {() => void} [props.onClickSuggestion]
 * @return {JSX.Element} The rendered search component.
 */
const Search = ({
  className,
  iconsPosition = 'right',
  switchableSearch = false,
  searchShow = true,
  setSearchShow,
  onClickSuggestion,
}) => {
  const isMobile = useIsMobileLayout();
  const dispatch = useDispatch();
  const favoriteHabitats = useSelector(selectFavoriteHabitatIds);
  const selectSortedHabitats = useMemo(() => createSortedHabitatsSelector(), []);
  const zoos = useSelector(selectZoosFromHabitats);
  const sortedHabitats = useSelector(selectSortedHabitats);
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [value, setValue] = useState('');
  const boxRef = useRef();
  const lastFetchRef = useRef();
  const { width: windowWidth } = useWindowResize();
  const { get, response } = useFetch(buildURL('/habitats/all'), {
    credentials: 'include',
    cachePolicy: 'no-cache',
  });
  const onClickSuggestionRef = useRef(onClickSuggestion);
  useEffect(() => {
    onClickSuggestionRef.current = onClickSuggestion;
  }, [onClickSuggestion]);

  const onSuggestionsOpen = useCallback(() => setSuggestionOpen(true), []);
  const onSuggestionsClose = useCallback(() => setSuggestionOpen(false), []);

  const onSearchFocus = async () => {
    const now = new Date();
    // refresh habitats on focus if data is older than a minute
    if (!lastFetchRef.current || differenceInMinutes(now, lastFetchRef.current) > 1) {
      lastFetchRef.current = now;
      await get();
      if (response.ok) {
        dispatch(setHabitats(response.data.habitats));
      }
    }
  };

  const handleSearchShow = () => {
    setSearchShow?.(true);
    setTimeout(() => {
      boxRef.current.querySelector('input').focus();
    });
  };

  const onChange = useCallback(({ target }) => {
    setValue(target.value);
  }, []);

  const suggestions = useMemo(() => {
    const input = value.trim().toLowerCase();
    const habitats = searchHabitats(_.chain(sortedHabitats), input, isMobile ? favoriteHabitats : undefined)
      .slice(0, 5)
      .map(([habitat]) => ({ data: habitat, type: 'habitat' }))
      .value();

    const filteredZoos = input
      ? zoos.filter((zoo) => zoo.name.toLowerCase().includes(input)).map((zoo) => ({ data: zoo, type: 'zoo' }))
      : [];

    return [...filteredZoos, ...habitats].slice(0, 5).map(({ data, type }, ind, list) => ({
      value: `${type}-${data._id}`,
      onClick: () => {
        if (type === 'habitat') {
          routeToHabitat({ id: data._id, slug: data.slug, zooSlug: data.zoo.slug }, { entryPoint: 'search-result' });
          trackMP('visit-habitat-link-clicked', {
            habitatId: data._id,
            entryPoint: 'search-result',
          });
        } else {
          route(`/h/${data.slug}`);
        }

        setValue('');
        onClickSuggestionRef.current?.();
      },
      label: (
        <div
          className={clsx(
            'preflight flex items-center gap-1.5 p-3',
            ind < list.length - 1 ? 'border-b border-black/35' : '',
          )}
        >
          <div className="size-[30px] shrink-0">
            <img
              src={type === 'habitat' ? data.profileImage : data.logo}
              alt={type === 'habitat' ? data.animal : data.name}
              className={clsx(
                'preflight preflight-img rounded-full bg-white',
                style.img,
                type === 'habitat' ? 'object-cover' : 'object-contain shadow-[0_2px_4px_0_rgba(0,0,0,0.25)]',
                windowWidth >= 460 || type !== 'habitat'
                  ? undefined
                  : data.liveTalk
                  ? style.liveTalk
                  : data.isStreamOn
                  ? style.online
                  : style.offline,
              )}
            />
          </div>
          <span className="line-clamp-2 min-w-0 grow text-sm font-medium">
            {type === 'habitat' ? data.animal : data.name}
          </span>
          {type === 'habitat' && (
            <div className="mr-1.5 shrink-0">
              <HabitatStatus online={data.isStreamOn} liveTalk={data.liveTalk} />
            </div>
          )}
        </div>
      ),
    }));
  }, [value, sortedHabitats, windowWidth, zoos, favoriteHabitats, isMobile]);

  const icons = switchableSearch ? (
    <Box onClick={() => setSearchShow?.(false)} className={style.searchClickIndicator}>
      <FontAwesomeIcon icon={faTimes} color="var(--grey)" style={{ width: '14px' }} />
    </Box>
  ) : (
    <Box onClick={() => boxRef.current.querySelector('input').focus()} className={style.searchClickIndicator}>
      <FontAwesomeIcon icon={faSearch} color="var(--grey)" style={{ width: '14px' }} />
    </Box>
  );

  /** @type {import('grommet').TextInputProps['onSelect']} */
  const onSelect = (e) => {
    e.suggestion.onClick?.();
    boxRef.current.querySelector('input').blur();
  };

  return (
    <Grommet theme={theme} className="flex-grow">
      {searchShow && (
        <Box
          ref={boxRef}
          direction="row"
          align="center"
          round="20px"
          className={clsx(style.searchWrapper, className)}
          pad={{ horizontal: 'small' }}
          elevation={suggestionOpen ? 'medium' : undefined}
          border={{
            side: 'all',
            color: suggestionOpen ? 'transparent' : '#CDCDCD',
          }}
          style={
            suggestionOpen
              ? {
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px',
                  borderBottomLeftRadius: '0px',
                  borderBottomRightRadius: '0px',
                }
              : undefined
          }
        >
          {iconsPosition === 'left' && icons}
          <TextInput
            name="search-habitat"
            autoComplete="off"
            dropTarget={boxRef.current}
            dropProps={{
              ref: (element) => {
                const rect = boxRef.current?.getBoundingClientRect();
                if (element && rect) {
                  // eslint-disable-next-line no-param-reassign
                  element.style.width = `${rect.width}px`;
                  // eslint-disable-next-line no-param-reassign
                  element.style.left = `${rect.left}px`;
                }
              },
              ...(isMobile ? { style: { zIndex: 31 } } : undefined),
            }}
            placeholder="Search for an animal..."
            plain
            suggestions={suggestions}
            value={value}
            onChange={onChange}
            onSuggestionsOpen={onSuggestionsOpen}
            onSuggestionsClose={onSuggestionsClose}
            onFocus={onSearchFocus}
            onSelect={onSelect}
            onKeyDown={(e) => {
              // Only trigger when there is only one result and the user presses enter
              // If there are more than one result, the user can use the up and down arrows to select the desired result
              if (e.key === 'Enter' && suggestions.length === 1) {
                [e.suggestion] = suggestions;
                onSelect(e);
              }
            }}
            style={{ padding: '7px', fontWeight: '500' }}
            dropHeight="medium"
          />
          {iconsPosition === 'right' && icons}
        </Box>
      )}
      {!searchShow && (
        <Box onClick={handleSearchShow} className={style.searchClickIndicator} align="end">
          <FontAwesomeIcon icon={faSearch} color="var(--grey)" style={{ width: '14px' }} />
        </Box>
      )}
    </Grommet>
  );
};

export default memo(Search);
